import { formatMoney, GRAMS_PER_TOLA } from "@/lib/gold-data";
import { Coins, Gem } from "lucide-react";

type CurrencyCode = "PKR" | "INR";

type GoldUnitPrices = {
  perGram24: number;
  perGram22: number;
  perGram21: number;
  perTola24: number;
  perTola22: number;
  perTola21: number;
  per10g24: number;
  per10g22: number;
  per10g21: number;
};

const KARAT_THEME: Record<
  "24K" | "22K" | "21K" | "18K",
  { ring: string; chip: string; halo: string; badge: string }
> = {
  "24K": {
    ring: "ring-amber-400/40 border-amber-400/40",
    chip: "bg-amber-500/15 text-amber-600 dark:text-amber-300",
    halo: "from-amber-400/20 via-yellow-300/10 to-transparent",
    badge: "Purest · 99.9%",
  },
  "22K": {
    ring: "ring-yellow-500/30 border-yellow-500/30",
    chip: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-300",
    halo: "from-yellow-500/15 via-amber-400/5 to-transparent",
    badge: "Jewellery · 91.6%",
  },
  "21K": {
    ring: "ring-orange-400/30 border-orange-400/30",
    chip: "bg-orange-500/15 text-orange-700 dark:text-orange-300",
    halo: "from-orange-400/15 via-amber-300/5 to-transparent",
    badge: "Popular · 87.5%",
  },
  "18K": {
    ring: "ring-rose-400/30 border-rose-400/30",
    chip: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
    halo: "from-rose-400/15 via-pink-300/5 to-transparent",
    badge: "Fine · 75.0%",
  },
};

export function PremiumGoldCards({
  prices,
  currency,
  regionLabel,
}: {
  prices: GoldUnitPrices;
  currency: CurrencyCode;
  regionLabel: string;
}) {
  // 18K = 24K * 0.75 (computed locally to keep the source of truth in gold-data)
  const perGram18 = prices.perGram24 * 0.75;
  const perTola18 = prices.perTola24 * 0.75;
  const per10g18 = prices.per10g24 * 0.75;

  const cards: Array<{
    karat: "24K" | "22K" | "21K" | "18K";
    perGram: number;
    perTola: number;
    per10g: number;
  }> = [
    { karat: "24K", perGram: prices.perGram24, perTola: prices.perTola24, per10g: prices.per10g24 },
    { karat: "22K", perGram: prices.perGram22, perTola: prices.perTola22, per10g: prices.per10g22 },
    { karat: "21K", perGram: prices.perGram21, perTola: prices.perTola21, per10g: prices.per10g21 },
    { karat: "18K", perGram: perGram18, perTola: perTola18, per10g: per10g18 },
  ];

  return (
    <section aria-label={`Gold prices in ${regionLabel} by karat & unit`} className="mt-8">
      <header className="mb-4 flex items-center gap-2">
        <span className="inline-flex items-center justify-center rounded-xl bg-amber-500/10 p-2 text-amber-500 ring-1 ring-amber-400/30">
          <Coins className="h-5 w-5" />
        </span>
        <div>
          <h2 className="text-lg md:text-xl font-bold tracking-tight">
            Gold Price Cards — {regionLabel}
          </h2>
          <p className="text-xs text-muted-foreground">
            Live 24K · 22K · 21K · 18K · per 1g, 10g & 1 tola
          </p>
        </div>
      </header>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => {
          const theme = KARAT_THEME[c.karat];
          return (
            <article
              key={c.karat}
              className={`group relative overflow-hidden rounded-2xl border bg-card/70 p-5 ring-1 ${theme.ring} shadow-sm hover:shadow-xl transition-all hover:-translate-y-0.5`}
            >
              <div
                aria-hidden
                className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${theme.halo} opacity-70`}
              />
              <div className="relative flex items-center justify-between">
                <div className="text-2xl font-black tracking-tight">{c.karat}</div>
                <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${theme.chip}`}>
                  {theme.badge}
                </span>
              </div>
              <div className="relative mt-4 space-y-2.5">
                <UnitRow label="Per 1 Gram" value={formatMoney(c.perGram, currency)} />
                <UnitRow label="Per 10 Grams" value={formatMoney(c.per10g, currency)} highlight />
                <UnitRow
                  label={`Per 1 Tola (${GRAMS_PER_TOLA}g)`}
                  value={formatMoney(c.perTola, currency)}
                  highlight
                />
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

type SilverUnitPrices = {
  perGram: number;
  per10g: number;
  perTola: number;
  perKg: number;
};

export function PremiumSilverCards({
  prices,
  currency,
  regionLabel,
  formatter = (n: number, c: CurrencyCode) => formatMoney(n, c),
}: {
  prices: SilverUnitPrices;
  currency: CurrencyCode;
  regionLabel: string;
  formatter?: (n: number, c: CurrencyCode) => string;
}) {
  const cards: Array<{ unit: string; value: number; badge: string; theme: keyof typeof SILVER_THEMES }> = [
    { unit: "Per 1 Gram", value: prices.perGram, badge: "Daily Use", theme: "slate" },
    { unit: "Per 10 Grams", value: prices.per10g, badge: "Popular", theme: "zinc" },
    { unit: `Per 1 Tola (${GRAMS_PER_TOLA}g)`, value: prices.perTola, badge: "Bullion", theme: "indigo" },
    { unit: "Per 1 Kilogram", value: prices.perKg, badge: "Wholesale", theme: "violet" },
  ];
  return (
    <section aria-label={`Silver prices in ${regionLabel} by unit`} className="mt-8">
      <header className="mb-4 flex items-center gap-2">
        <span className="inline-flex items-center justify-center rounded-xl bg-slate-500/10 p-2 text-slate-500 ring-1 ring-slate-400/30">
          <Gem className="h-5 w-5" />
        </span>
        <div>
          <h2 className="text-lg md:text-xl font-bold tracking-tight">
            Silver Price Cards — {regionLabel}
          </h2>
          <p className="text-xs text-muted-foreground">Live chandi rate · gram, 10 gram, tola & kilogram</p>
        </div>
      </header>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => {
          const theme = SILVER_THEMES[c.theme];
          return (
            <article
              key={c.unit}
              className={`group relative overflow-hidden rounded-2xl border bg-card/70 p-5 ring-1 ${theme.ring} shadow-sm hover:shadow-xl transition-all hover:-translate-y-0.5`}
            >
              <div
                aria-hidden
                className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${theme.halo} opacity-70`}
              />
              <div className="relative flex items-center justify-between">
                <div className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                  {c.unit}
                </div>
                <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${theme.chip}`}>
                  {c.badge}
                </span>
              </div>
              <div className="relative mt-3 text-2xl md:text-3xl font-extrabold tracking-tight text-foreground">
                {formatter(c.value, currency)}
              </div>
              <div className="relative mt-1 text-xs text-muted-foreground">
                Live silver · {regionLabel}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

const SILVER_THEMES = {
  slate: {
    ring: "ring-slate-400/30 border-slate-400/30",
    chip: "bg-slate-500/15 text-slate-600 dark:text-slate-300",
    halo: "from-slate-400/15 via-zinc-300/5 to-transparent",
  },
  zinc: {
    ring: "ring-zinc-400/30 border-zinc-400/30",
    chip: "bg-zinc-500/15 text-zinc-700 dark:text-zinc-300",
    halo: "from-zinc-400/15 via-slate-300/5 to-transparent",
  },
  indigo: {
    ring: "ring-indigo-400/30 border-indigo-400/30",
    chip: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300",
    halo: "from-indigo-400/15 via-blue-300/5 to-transparent",
  },
  violet: {
    ring: "ring-violet-400/30 border-violet-400/30",
    chip: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
    halo: "from-violet-400/15 via-fuchsia-300/5 to-transparent",
  },
} as const;

function UnitRow({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-background/60 px-3 py-2 backdrop-blur">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className={`tabular-nums ${highlight ? "text-base font-extrabold text-foreground" : "text-sm font-bold text-foreground/90"}`}>
        {value}
      </span>
    </div>
  );
}